import React from 'react';
import PropTypes from 'prop-types';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { LineChart } from 'echarts/charts';
import { GridComponent, TooltipComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { Card } from 'components/common';

// Modular registration, matching the CRM report charts — importing the full
// echarts bundle would pull ~1MB into the main chunk for one line chart.
echarts.use([LineChart, GridComponent, TooltipComponent, CanvasRenderer]);

/**
 * Sign-ins over time.
 *
 * The reference design had a "who's online, sampled every 5 minutes" series.
 * That needs a periodic sampler writing snapshots, which was deliberately not
 * built — this is derived from signed_in_at instead, and every bucket in the
 * window is present including the empty ones, so quiet periods stay visible
 * rather than being compressed out of the line.
 */
const UNITS = [
  { key: 'hour', label: 'Hour' },
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
];

const SignInTrendChart = ({
  title, subtitle, series, color = '#2563eb',
  granularity, available = [], onGranularityChange = () => {},
}) => {
  const option = {
    grid: { top: 24, bottom: 34, left: 40, right: 16 },
    tooltip: {
      trigger: 'axis',
      formatter: (params) => {
        const point = params[0];
        return `${point.name}<br/><b>${point.value}</b> sign-in(s)`;
      },
    },
    xAxis: {
      type: 'category',
      data: series.map((row) => row.label),
      axisTick: { show: false },
      axisLine: { lineStyle: { color: '#e2e8f0' } },
      axisLabel: { color: '#94a3b8', fontSize: 11 },
    },
    yAxis: {
      type: 'value',
      minInterval: 1, // sign-ins are whole numbers; "0.5 sign-ins" is nonsense
      splitLine: { lineStyle: { color: '#f1f5f9', type: 'dashed' } },
      axisLine: { show: false },
      axisLabel: { color: '#94a3b8', fontSize: 11 },
    },
    series: [{
      type: 'line',
      smooth: true,
      symbol: 'circle',
      symbolSize: 5,
      data: series.map((row) => row.count),
      lineStyle: { width: 2.5, color },
      itemStyle: { color, borderColor: '#fff', borderWidth: 2 },
      areaStyle: { color, opacity: 0.08 },
    }],
  };

  return (
    <Card
      padding="none"
      header={(
        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-w-full">
          <div>
            <h6 className="tw-mb-0 tw-font-semibold tw-text-slate-700">{title}</h6>
            {subtitle && <p className="tw-text-xs tw-text-slate-400 tw-m-0 tw-mt-0.5">{subtitle}</p>}
          </div>

          {/* Only the units that fit the range in view, decided by the server.
              A greyed-out or silently-ignored button teaches people the control
              is unreliable; showing three real choices teaches them nothing at
              all, which is the point. */}
          {available.length > 1 && (
            <div className="tw-inline-flex tw-p-0.5 tw-rounded tw-bg-slate-100">
              {UNITS.filter((unit) => available.includes(unit.key)).map((unit) => (
                <button
                  key={unit.key}
                  type="button"
                  onClick={() => onGranularityChange(unit.key)}
                  aria-pressed={granularity === unit.key}
                  className={[
                    'tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold tw-rounded-md tw-border-0 tw-transition-colors',
                    granularity === unit.key
                      ? 'tw-bg-white tw-text-slate-800 tw-shadow-sm'
                      : 'tw-bg-transparent tw-text-slate-500 hover:tw-text-slate-700',
                  ].join(' ')}
                >
                  {unit.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    >
      <div className="tw-p-3">
        <ReactEChartsCore
          echarts={echarts}
          option={option}
          notMerge
          lazyUpdate
          style={{ height: 240, width: '100%' }}
        />
      </div>
    </Card>
  );
};

SignInTrendChart.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  series: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    count: PropTypes.number,
  })).isRequired,
  color: PropTypes.string,
  granularity: PropTypes.string,
  available: PropTypes.arrayOf(PropTypes.string),
  onGranularityChange: PropTypes.func,
};

export default SignInTrendChart;
